'use client';
import Image from 'next/image';
import React, { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import Modal from '@/app/ui/components/filters/mobile/Modal';
import useModal from '@/app/helpers/hooks/useModal';
import clsx from 'clsx';
import { getCurrentLocalePath } from '@/app/helpers/locale';
import { COOKIE_NAMES, GENDER } from '@/app/constants';
import Link from 'next/link';
import { getCookie } from 'cookies-next';
import { MenuType } from '@mybestbrands/types';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  categories: MenuType[] | undefined | null;
}

export function CategoriesModal(props: Props) {
  const { isVisible, onClose, categories } = props;
  const { isModalOpen, openModal, closeModal } = useModal(isVisible);
  const selectedGender = getCookie(COOKIE_NAMES.GENDER);

  const { t, i18n } = useTranslation(['filters', 'navigation']);
  const localePath = getCurrentLocalePath(i18n.language);

  useEffect(() => {
    if (isVisible) {
      openModal();
    }
  }, [isVisible]);

  function onCloseModal() {
    onClose();
    closeModal();
  }

  function getThirdLevel(item: MenuType) {
    if (!item.children || !item.children.length) return null;

    const anyChildrenSelected = item.children.some((subItem) => subItem.active);
    return (
      <ul className={clsx(item.active || anyChildrenSelected ? 'd-block' : 'd-none')}>
        {item?.children?.map((item) => {
          const isActive = item?.active;

          return (
            <li key={item.id} className="link-sidebar">
              <Link href={`${item.url}`} className={clsx('mb-2 text-decoration-none', isActive ? 'fw-bold' : '')}>
                <span className="link">{item.name}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <>
      {isModalOpen && (
        <Modal>
          <div className="bg-white w-100 h-100 fixed-top overflow-auto">
            <div className="d-flex aligncenter justify-content-between mt-3 mx-4">
              <span className="aligncenter" style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                {t('categories')}
              </span>
              <Image onClick={onCloseModal} src="/intstatic/icons/close.svg" alt="Close" height={14} width={14} />
            </div>
            <div className="bg-body-secondary w-100 mt-3 mb-4" style={{ height: '1px' }}></div>
            <div className="mx-4">
              <div style={{ height: 774, paddingBottom: 250 }}>
                <div className="col-12">
                  <ul className="p-0" style={{ listStyleType: 'none' }}>
                    <>
                      {categories?.map((item) => {
                        if (item.children.length < 1) return null;
                        const isActive = item.active || item.children.some((subItem) => subItem.active);

                        return (
                          <li key={item.slug} className="link-sidebar">
                            <Link
                              onClick={() => onCloseModal()}
                              href={`${localePath}${item.url}`}
                              className={clsx('mb-2 text-decoration-none', isActive ? 'fw-bold' : '')}>
                              <span className="link">{item.name}</span>
                            </Link>
                            <ul className={clsx('p-2', isActive ? '' : 'd-none')}>
                              {item.children
                                .filter((subItem) => {
                                  // show only gender-specific items
                                  if (!selectedGender) return true;
                                  return subItem.gender === GENDER.NEUTRAL || subItem.gender === selectedGender;
                                })
                                .map((subItem) => {
                                  const thirdLevel = getThirdLevel(subItem);
                                  return (
                                    <li key={subItem.id} className="link-sidebar">
                                      <Link
                                        onClick={() => onCloseModal()}
                                        href={`${localePath}${subItem.url}`}
                                        className={clsx('mb-2 text-decoration-none', subItem.active ? 'fw-bold' : '')}>
                                        <span className="link">{subItem.name}</span>
                                      </Link>
                                      {thirdLevel}
                                    </li>
                                  );
                                })}
                            </ul>
                          </li>
                        );
                      })}
                    </>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
